import { createPrintJob, type PreviewOptions } from "../../src/index";

// This example demonstrates a dense sheet of repeated labels so users can see
// how compact custom-size content behaves when many cards share one page.
export function createLabelSheetExample() {
  const job = createPrintJob({
    title: "Label Sheet",
    pageSize: "A4",
    orientation: "portrait",
    margins: { top: "10mm", right: "10mm", bottom: "10mm", left: "10mm" },
    fontFamily: '"Segoe UI", "PingFang SC", sans-serif'
  });

  job.addText("Label Sheet", {
    tagName: "h1",
    style: {
      margin: 0,
      fontSize: "26px",
      letterSpacing: "0.03em"
    }
  });

  job.addHtml(
    `<p style="margin:0;color:#475569;font-size:13px;line-height:1.6;">
      This example simulates a multi-label sheet where the same reusable card
      structure repeats across a standard page. It is useful for verifying small
      layouts, spacing rhythm, and dense content packing.
    </p>`
  );

  const labels = Array.from({ length: 16 }, (_, index) => {
    return `<div style="border:1px solid #cbd5e1;border-radius:18px;padding:12px;display:grid;gap:6px;min-height:126px;background:#ffffff;">
      <div style="display:flex;justify-content:space-between;gap:8px;font-size:11px;color:#64748b;">
        <span>Label ${index + 1}</span>
        <span>Rack ${String.fromCharCode(65 + (index % 4))}-${(index % 2) + 1}</span>
      </div>
      <div style="font-size:16px;font-weight:700;letter-spacing:0.03em;">ITEM-${1000 + index}</div>
      <div style="font-size:12px;color:#334155;line-height:1.5;">Thermal shipping label bundle for warehouse bin handoff.</div>
      <div style="margin-top:auto;font-size:11px;color:#64748b;">Batch: B-${20260610 + index}</div>
    </div>`;
  });

  // Emit each label row as its own top-level block so estimated pagination can
  // keep early rows on page 1 instead of treating the whole sheet as one giant
  // indivisible HTML container.
  for (let index = 0; index < labels.length; index += 2) {
    const left = labels[index] ?? "";
    const right = labels[index + 1] ?? "";

    job.addHtml(
      `<div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;${index > 0 ? "margin-top:10px;" : "margin-top:8px;"}">
        ${left}
        ${right}
      </div>`
    );
  }

  const previewOptions: Omit<PreviewOptions, "container"> = {
    mode: "document"
  };

  return {
    id: "label-sheet",
    title: "Label Sheet",
    description: "A dense reusable label grid that demonstrates repeated small-format layouts on a standard sheet.",
    job,
    defaultPreviewOptions: previewOptions
  };
}
